import React from "react"

// Generic property editor components
import TextPropertyEditor from "./property-editors/TextPropertyEditor"
import ColorInputPropertyEditor from "./property-editors/ColorInputPropertyEditor"
import SelectPropertyEditor from "./property-editors/SelectPropertyEditor"
import ImagePropertyEditor from "./property-editors/ImagePropertyEditor"

// Generic property group editor (renders based on property definitions)
import GenericPropertyGroupEditor from "./property-editors/GenericPropertyGroupEditor"

// Special editors
import ModifiersEditor from "./components/ModifiersEditor"

/**
 * PropertyEditorResolver - Maps property definitions to their appropriate editor components
 * Similar to CMS field-editor-resolver.jsx pattern but for CSS property editing
 */
class PropertyEditorResolver {
  constructor() {
    // Registry of property editor components
    this.propertyEditors = {
      // Generic property type editors for individual properties
      text: TextPropertyEditor,
      color: ColorInputPropertyEditor,
      select: SelectPropertyEditor,
      image: ImagePropertyEditor,
      modifiers: ModifiersEditor,
    }
  }

  /**
   * Get the appropriate editor component for a property definition
   * @param {Object} propertyDef - Property definition from property-definitions.js
   * @param {string} groupKey - Property group key (background, typography, etc.)
   * @returns {React.Component} - Editor component
   */
  getEditor(propertyDef, groupKey) {
    // First check if there's a specific editor specified
    if (propertyDef.editor && this.propertyEditors[propertyDef.editor]) {
      return this.propertyEditors[propertyDef.editor]
    }

    // Then check for property type-based editor
    if (propertyDef.type && this.propertyEditors[propertyDef.type]) {
      return this.propertyEditors[propertyDef.type]
    }

    // If it has properties (property group), use generic property group editor
    if (propertyDef.properties) {
      return GenericPropertyGroupEditor
    }

    // Fallback to text editor for individual properties
    return this.propertyEditors.text
  }

  /**
   * Render a property editor component with the given props
   * @param {Object} propertyDef - Property definition
   * @param {string} groupKey - Property group key
   * @param {Object} props - Props to pass to the editor
   * @returns {React.Element} - Rendered editor component
   */
  renderEditor(propertyDef, groupKey, props) {
    const EditorComponent = this.getEditor(propertyDef, groupKey)

    if (!EditorComponent) {
      return null
    }

    // Pass through all props to the editor component
    return React.createElement(EditorComponent, {
      key: groupKey,
      group: propertyDef, // Pass the property definition as 'group' prop
      ...props
    })
  }

  /**
   * Register a new property editor
   * @param {string} type - Property type (text, color, select, etc.)
   * @param {React.Component} component - Editor component
   */
  registerEditor(type, component) {
    this.propertyEditors[type] = component
  }

  /**
   * Register a custom editor component
   * @param {string} editorName - Editor component name
   * @param {React.Component} component - Editor component
   */
  registerCustomEditor(editorName, component) {
    this.propertyEditors[editorName] = component
  }

  /**
   * Get all registered editor types and names
   * @returns {Array} - Array of editor type names
   */
  getEditorTypes() {
    return Object.keys(this.propertyEditors)
  }

  /**
   * Get all registered custom editors (non-type based)
   * @returns {Array} - Array of custom editor names
   */
  getCustomEditors() {
    const typeBasedEditors = ['text', 'color', 'select', 'image', 'modifiers']
    return Object.keys(this.propertyEditors).filter(key => !typeBasedEditors.includes(key))
  }
}

// Create singleton instance
const propertyEditorResolver = new PropertyEditorResolver()

export default propertyEditorResolver